import type { NodeTypeDef } from '../core/types';

/** Nodes that move data between RGBA channels. */
export const channelNodes: NodeTypeDef[] = [
  {
    id: 'channel.extract',
    label: 'Extract Channel',
    category: 'channel',
    description: 'Pull one channel — or luminance — out as grayscale.',
    guidance:
      'Bridges colour work back into mask work. Extracting luminance from a base colour gives a value structure you can feed straight into Levels or Normal.',
    keywords: ['split', 'grayscale', 'luminance', 'red', 'green', 'blue', 'alpha', 'desaturate'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'grayscale',
    params: [
      {
        name: 'channel',
        label: 'Channel',
        type: 'enum',
        default: 'luminance',
        options: [
          { value: 'luminance', label: 'Luminance' },
          { value: 'red', label: 'Red' },
          { value: 'green', label: 'Green' },
          { value: 'blue', label: 'Blue' },
          { value: 'alpha', label: 'Alpha' },
          { value: 'average', label: 'Average' },
          { value: 'max', label: 'Max' },
          { value: 'min', label: 'Min' },
        ],
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec4 c = in0(uv);
        float v;
        if (p_channel == K_channel_luminance) v = lum(c.rgb);
        else if (p_channel == K_channel_red) v = c.r;
        else if (p_channel == K_channel_green) v = c.g;
        else if (p_channel == K_channel_blue) v = c.b;
        else if (p_channel == K_channel_alpha) v = c.a;
        else if (p_channel == K_channel_average) v = (c.r + c.g + c.b) / 3.0;
        else if (p_channel == K_channel_max) v = max(c.r, max(c.g, c.b));
        else v = min(c.r, min(c.g, c.b));
        return vec4(vec3(v), 1.0);
      }
    `,
  },

  {
    id: 'channel.combine',
    label: 'Combine Channels',
    category: 'channel',
    description: 'Assemble RGBA from up to four grayscale inputs.',
    guidance:
      'Packs masks into a single texture — the standard way to ship roughness, metallic, and AO in one file. Unconnected inputs fall back to their default constant.',
    keywords: ['merge', 'pack', 'rgba', 'orm', 'mask packing', 'assemble'],
    inputs: [
      { name: 'red', label: 'Red', kind: 'grayscale', optional: true, fallback: [0, 0, 0, 1] },
      { name: 'green', label: 'Green', kind: 'grayscale', optional: true, fallback: [0, 0, 0, 1] },
      { name: 'blue', label: 'Blue', kind: 'grayscale', optional: true, fallback: [0, 0, 0, 1] },
      { name: 'alpha', label: 'Alpha', kind: 'grayscale', optional: true, fallback: [1, 1, 1, 1] },
    ],
    output: 'color',
    params: [],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        return vec4(in0g(uv), in1g(uv), in2g(uv), in3g(uv));
      }
    `,
  },

  {
    id: 'channel.swizzle',
    label: 'Swizzle',
    category: 'channel',
    description: 'Reorder or duplicate channels.',
    guidance:
      "Fixes channel-order mismatches between tools, and flips a normal map's green channel without a full rebuild.",
    keywords: ['reorder', 'shuffle', 'rgba', 'flip green', 'remap'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'color',
    params: [
      { name: 'r', label: 'Red from', type: 'enum', default: 'r', options: sourceOptions() },
      { name: 'g', label: 'Green from', type: 'enum', default: 'g', options: sourceOptions() },
      { name: 'b', label: 'Blue from', type: 'enum', default: 'b', options: sourceOptions() },
      { name: 'a', label: 'Alpha from', type: 'enum', default: 'a', options: sourceOptions() },
    ],
    shader: /* glsl */ `
      float pick(vec4 c, int src) {
        if (src == 0) return c.r;
        if (src == 1) return c.g;
        if (src == 2) return c.b;
        if (src == 3) return c.a;
        if (src == 4) return 0.0;
        if (src == 5) return 1.0;
        if (src == 6) return 1.0 - c.r;
        if (src == 7) return 1.0 - c.g;
        if (src == 8) return 1.0 - c.b;
        return 1.0 - c.a;
      }

      vec4 texdir(vec2 uv) {
        vec4 c = in0(uv);
        return vec4(pick(c, p_r), pick(c, p_g), pick(c, p_b), pick(c, p_a));
      }
    `,
  },
  {
    id: 'channel.mixer',
    label: 'Channel Mixer',
    category: 'channel',
    description: 'Build each output channel from a weighted sum of the inputs.',
    guidance:
      'A full matrix mix, where Swizzle only reorders. Use it for custom grayscale conversions, colour grading that Levels cannot express, and rebalancing a normal map. Weights may be negative, and rows need not sum to one.',
    keywords: ['matrix', 'weights', 'mix', 'grade', 'grayscale', 'rebalance', 'rgb'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'color',
    params: [
      {
        name: 'red',
        label: 'Red from RGB',
        type: 'vec3',
        default: [1, 0, 0],
        min: -2,
        max: 2,
        step: 0.01,
        group: 'Red',
      },
      {
        name: 'green',
        label: 'Green from RGB',
        type: 'vec3',
        default: [0, 1, 0],
        min: -2,
        max: 2,
        step: 0.01,
        group: 'Green',
      },
      {
        name: 'blue',
        label: 'Blue from RGB',
        type: 'vec3',
        default: [0, 0, 1],
        min: -2,
        max: 2,
        step: 0.01,
        group: 'Blue',
      },
      {
        name: 'offset',
        label: 'Offset',
        type: 'vec3',
        default: [0, 0, 0],
        min: -1,
        max: 1,
        step: 0.001,
        group: 'Output',
        help: 'Added after mixing, per channel.',
      },
      {
        name: 'monochrome',
        label: 'Monochrome',
        type: 'bool',
        default: false,
        group: 'Output',
        help: 'Applies the Red row to every channel, which is how a custom grayscale conversion is built.',
      },
      {
        name: 'clampOutput',
        label: 'Clamp Output',
        type: 'bool',
        default: true,
        group: 'Output',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec4 c = in0(uv);
        vec3 mixed = vec3(
          dot(c.rgb, p_red),
          dot(c.rgb, p_green),
          dot(c.rgb, p_blue)
        );
        if (p_monochrome) mixed = vec3(dot(c.rgb, p_red));
        mixed += p_offset;
        if (p_clampOutput) mixed = saturate(mixed);
        return vec4(mixed, c.a);
      }
    `,
  },
];

/** Channel sources for Swizzle. Order is the `pick()` contract in GLSL. */
function sourceOptions() {
  return [
    { value: 'r', label: 'Red' },
    { value: 'g', label: 'Green' },
    { value: 'b', label: 'Blue' },
    { value: 'a', label: 'Alpha' },
    { value: 'zero', label: 'Zero' },
    { value: 'one', label: 'One' },
    { value: 'invr', label: '1 − Red' },
    { value: 'invg', label: '1 − Green' },
    { value: 'invb', label: '1 − Blue' },
    { value: 'inva', label: '1 − Alpha' },
  ];
}
